import React from 'react';
import { Button } from '../Button';
import { Select } from '../Select';
import styles from './Pagination.module.scss';

export interface PaginationProps {
    currentPage: number;
    totalPages: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    rowsPerPageOptions?: number[];
    showInfo?: boolean;
    className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [5, 10, 25, 50],
    showInfo = true,
    className = '',
}) => {
    // Handler for next page
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    // Handler for previous page
    const handlePrevPage = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    // Handler for specific page click
    const handlePageClick = (page: number) => {
        onPageChange(page);
    };

    // Handler for changing rows per page
    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newRowsPerPage = parseInt(e.target.value, 10);
        onRowsPerPageChange(newRowsPerPage);
    };

    // Prepare options for rows per page select
    const rowsPerPageOptionsList = rowsPerPageOptions.map((option) => ({
        value: option.toString(),
        label: option.toString(),
    }));

    // Generate page numbers to display
    const generatePageNumbers = () => {
        const pages: (number | 'ellipsis')[] = [];
        const maxVisiblePages = 7; // Show more pages for better UX

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages <= 7
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Determine the range around current page
            let startRange = Math.max(2, currentPage - 1);
            let endRange = Math.min(totalPages - 1, currentPage + 1);

            // Adjust range to show more pages when possible
            if (currentPage <= 3) {
                endRange = Math.min(totalPages - 1, 5);
            } else if (currentPage >= totalPages - 2) {
                startRange = Math.max(2, totalPages - 4);
            }

            // Add left ellipsis if needed
            if (startRange > 2) {
                pages.push('ellipsis');
            }

            // Add pages in range
            for (let i = startRange; i <= endRange; i++) {
                pages.push(i);
            }

            // Add right ellipsis if needed
            if (endRange < totalPages - 1) {
                pages.push('ellipsis');
            }

            // Always show last page (if not already included)
            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const visiblePages = generatePageNumbers();

    if (totalPages <= 1 && !showInfo) {
        return null; // Don't render pagination if there's only one page and no info to show
    }

    return (
        <div className={`${styles.paginationControls} ${className}`}>
            {/* Previous Button */}
            <Button
                variant='outline-secondary'
                size='md'
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                aria-label='Previous page'
                className={styles.prevBtn}
            >
                <span className={styles.arrow}>←</span> Previous
            </Button>

            {/* Page Numbers */}
            <div className={styles.pageNumbers}>
                {visiblePages.map((page, index) => {
                    if (page === 'ellipsis') {
                        return (
                            <span key={`ellipsis-${index}`} className={styles.pageEllipsis} aria-hidden='true'>
                                ...
                            </span>
                        );
                    }

                    return (
                        <span
                            key={page}
                            className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                            onClick={() => handlePageClick(page as number)}
                            aria-label={`Go to page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </span>
                    );
                })}
            </div>

            {/* Rows per page selector */}
            <div className={styles.rowsPerPage}>
                <span className={styles.rowsLabel}>Rows per page:</span>
                <Select
                    name='rowsPerPage'
                    options={rowsPerPageOptionsList}
                    value={rowsPerPage.toString()}
                    onChange={handleRowsPerPageChange}
                    size='md'
                    aria-label='Select rows per page'
                />
            </div>

            {/* Next Button */}
            <Button
                variant='outline-secondary'
                size='md'
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                aria-label='Next page'
                className={styles.nextBtn}
            >
                Next <span className={styles.arrow}>→</span>
            </Button>
        </div>
    );
};

export default Pagination;
